/** POST body for Job Manager `jobstatus` (music generation / stems). */
export interface MusicJobStatusPayload {
  jobID: string;
  jobTitle: number;
  description: string;
  status: number;
  tags: string;
  /** UTC ISO-8601 timestamp when the job was first enqueued (status 10). */
  ProcessInitiatedOn?: string;
  /** Elapsed seconds from ProcessInitiatedOn to status 30 completion. */
  TimeTakenInSec?: number;
  jobMetaData: {
    additionalProp1: string[];
    [key: string]: unknown;
  };
}

/** Timing fields returned by Job Manager on GET `jobstatus/{id}`. */
export interface JobTimingFields {
  processInitiatedOn?: string;
  ProcessInitiatedOn?: string;
  status10At?: string;
  Status10At?: string;
  status20At?: string;
  Status20At?: string;
  status30At?: string;
  Status30At?: string;
  timeTakenInSec?: number;
  TimeTakenInSec?: number;
  jobMetaData?: {
    processInitiatedOn?: string;
    ProcessInitiatedOn?: string;
    timeTakenInSec?: number;
    TimeTakenInSec?: number;
    [key: string]: unknown;
  };
}

/** Normalizes timing fields from Job Manager (PascalCase or camelCase). */
export function readJobTimingFields(
  job: JobTimingFields | null | undefined,
): {
  processInitiatedOn: string | undefined;
  timeTakenInSec: number | undefined;
} {
  if (!job) {
    return { processInitiatedOn: undefined, timeTakenInSec: undefined };
  }

  const meta = job.jobMetaData;

  const processInitiatedOn =
    job.processInitiatedOn ??
    job.ProcessInitiatedOn ??
    job.status10At ??
    job.Status10At ??
    meta?.processInitiatedOn ??
    meta?.ProcessInitiatedOn;

  const rawSeconds =
    job.timeTakenInSec ??
    job.TimeTakenInSec ??
    meta?.timeTakenInSec ??
    meta?.TimeTakenInSec;
  const timeTakenInSec =
    rawSeconds === undefined || rawSeconds === null
      ? undefined
      : Number(rawSeconds);

  return {
    processInitiatedOn,
    timeTakenInSec: Number.isFinite(timeTakenInSec) ? timeTakenInSec : undefined,
  };
}

/** Seconds elapsed since `processInitiatedOn` (UTC ISO string). */
export function computeTimeTakenInSec(processInitiatedOn: string): number {
  const startedAt = Date.parse(processInitiatedOn);
  if (!Number.isFinite(startedAt)) {
    return 0;
  }
  return Math.max(0, Math.round((Date.now() - startedAt) / 1000));
}
